import type {
  LeagueSummary,
  LeaderboardEntryPublic,
} from "@softmax/observatory-client/types";
import { catalogListedLeagues } from "@softmax/web-features/coworlds";

export function leagueTag(league: LeagueSummary): string {
  return league.game.coworld_name ?? league.game.name;
}

const aiOnlyClause = /,\s*ai(?:[\s-]\w+){0,3}?\s+only\s*(?=[.!?]?$)/i;

export function gameBrief(league: LeagueSummary): {
  premise: string;
  rest: string;
} | null {
  const description = league.game.description?.trim();

  if (!description) return null;

  const end = description.search(/[.!?](\s|$)/);
  const first = end === -1 ? description : description.slice(0, end + 1);
  const premise = first.replace(aiOnlyClause, "").trim();

  if (!premise) return null;

  return { premise, rest: description.slice(first.length).trim() };
}

type StandingsRowEncoding = {
  leader: boolean;
  interval: number | null;
};

function positiveScoreDistance({
  from,
  to,
}: {
  from: number;
  to: number;
}): number | null {
  return Number.isFinite(from) && Number.isFinite(to) && from - to > 0
    ? from - to
    : null;
}

export function standingsEncoding(
  entries: readonly LeaderboardEntryPublic[],
): Map<string, StandingsRowEncoding> {
  const encodings = new Map<string, StandingsRowEncoding>();
  let previous: LeaderboardEntryPublic | undefined;

  // The server's board order owns the ranking, even when scores are not monotonic.
  for (const entry of entries) {
    encodings.set(entry.player_id, {
      leader: previous === undefined,
      interval:
        previous === undefined
          ? null
          : positiveScoreDistance({ from: previous.score, to: entry.score }),
    });
    previous = entry;
  }

  return encodings;
}

type LeagueMenuOrder = {
  leagues: LeagueSummary[];
  coworldCount: number;
};

export function leagueMenuOrder({
  leagues,
  current,
}: {
  leagues: LeagueSummary[];
  current: LeagueSummary | null | undefined;
}): LeagueMenuOrder {
  const listed = catalogListedLeagues(leagues);

  if (!current) return { leagues: listed, coworldCount: 0 };

  const mine = leagues.filter((league) => league.game.id === current.game.id);
  const rest = listed.filter((league) => league.game.id !== current.game.id);
  return { leagues: [...mine, ...rest], coworldCount: mine.length };
}
